import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type Language = 'zh' | 'en' | 'ja';

interface TranslationEntry {
  zh: string;
  en: string;
  ja: string;
}

interface I18nContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  translateText: (text: string) => string;
  t: (key: string) => string;
}

const STORAGE_KEY = 'kendama-language';
const MANUAL_STORAGE_KEY = 'kendama-language-manual';

const isLanguage = (value: unknown): value is Language =>
  value === 'zh' || value === 'en' || value === 'ja';

/**
 * 系统语言 -> App 语言版本：简体中文与繁体中文统一使用中文，日语使用日文，其他语言统一使用英文。
 */
export const resolveLanguageFromLocale = (locale?: string | null): Language | null => {
  if (!locale) return null;
  const primary = locale.trim().toLowerCase().replace(/_/g, '-').split('-')[0];
  if (!primary) return null;
  if (primary === 'zh') return 'zh';
  if (primary === 'ja') return 'ja';
  return 'en';
};

// 浏览器 / WebView 暴露的系统语言，优先取语言偏好列表（navigator.languages）的第一项。
const detectBrowserLanguage = (): Language | null => {
  if (typeof navigator === 'undefined') return null;
  for (const candidate of [...(navigator.languages ?? []), navigator.language]) {
    const resolved = resolveLanguageFromLocale(candidate);
    if (resolved) return resolved;
  }
  return null;
};

// 原生 App 通过 Capacitor Device 插件读取设备语言：iOS 的 WebView 只会返回 App 支持的本地化语言，
// 仅读 navigator.language 拿不到设备语言，必须走原生接口。
const detectNativeLanguage = async (): Promise<Language | null> => {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (!Capacitor.isNativePlatform()) return null;
    const { Device } = await import('@capacitor/device');
    const { value } = await Device.getLanguageCode();
    return resolveLanguageFromLocale(value);
  } catch {
    return null;
  }
};

// 用户手动选择过的语言。自动判断的结果不写入存储，这样系统语言变化后仍能跟随系统。
const readRememberedLanguage = (): Language | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!isLanguage(stored)) return null;
    if (window.localStorage.getItem(MANUAL_STORAGE_KEY) === 'true') return stored;
    // 兼容旧版本：旧版每次启动都会把当前语言写入 STORAGE_KEY，默认值为 'zh'，
    // 因此没有手动标记的 'zh' 不能证明是用户的选择，交回系统语言判断；
    // 而 'en' / 'ja' 只可能来自用户手动切换，继续沿用。
    return stored === 'zh' ? null : stored;
  } catch {
    return null;
  }
};

const uiText: Record<string, TranslationEntry> = {
  language: { zh: '语言', en: 'Language', ja: '言語' },
  languageChinese: { zh: '中文', en: 'Chinese', ja: '中国語' },
  languageEnglish: { zh: '英文', en: 'English', ja: '英語' },
  languageJapanese: { zh: '日文', en: 'Japanese', ja: '日本語' },
  saveRoom: { zh: '保存房间', en: 'Save Room', ja: 'ルームを保存' },
  roomSaved: { zh: '已保存房间', en: 'Room Saved', ja: '保存済みルーム' },
  savingRoom: { zh: '保存中...', en: 'Saving...', ja: '保存中...' },
  deleteRoomAndExit: { zh: '退出并删除房间', en: 'Exit & Delete Room', ja: '退出してルーム削除' },
  deletingRoom: { zh: '删除中...', en: 'Deleting...', ja: '削除中...' },
  refresh: { zh: '刷新', en: 'Refresh', ja: '更新' },
  refreshScores: { zh: '刷新分数', en: 'Refresh Scores', ja: 'スコアを更新' },
  refreshVotes: { zh: '刷新票数', en: 'Refresh Votes', ja: '投票を更新' },
  refreshing: { zh: '刷新中', en: 'Refreshing', ja: '更新中' },
  close: { zh: '关闭', en: 'Close', ja: '閉じる' },
  loggingOut: { zh: '正在退出...', en: 'Logging out...', ja: 'ログアウト中...' },
  loggingOutHint: { zh: '正在清理登录状态，请稍候', en: 'Clearing your session, one moment', ja: 'ログイン情報を削除しています' }
};

const literalEntries: TranslationEntry[] = [
  { zh: '官方合作伙伴', en: 'Official Partner', ja: '公式パートナー' },
  { zh: '选择模式', en: 'Select Mode', ja: 'モード選択' },
  { zh: '准备对战', en: 'Ready to Duel', ja: '対戦準備完了' },
  { zh: '状态', en: 'Status', ja: '状態' },
  { zh: '管理后台', en: 'Admin Dashboard', ja: '管理ダッシュボード' },
  { zh: '个人中心', en: 'User Center', ja: 'ユーザーセンター' },
  { zh: '退出登录', en: 'Logout', ja: 'ログアウト' },
  { zh: '登录', en: 'Login', ja: 'ログイン' },
  { zh: '返回', en: 'Back', ja: '戻る' },
  { zh: '初始化中...', en: 'INITIALIZING...', ja: '初期化中...' },
  { zh: '对战', en: 'BATTLE', ja: '対戦' },
  { zh: '积分', en: 'POINT', ja: 'ポイント' },
  { zh: '自由式', en: 'FREESTYLE', ja: 'フリースタイル' },
  { zh: '联机', en: 'ONLINE', ja: 'オンライン' },
  { zh: '经典 1v1 卡牌对战', en: 'Classic 1v1 Card Duel', ja: 'クラシック 1v1 カード対戦' },
  { zh: '积分竞速模式', en: 'Score Attack Mode', ja: 'ポイントアタックモード' },
  { zh: '裁判面板模式', en: 'Judge Panel Mode', ja: 'ジャッジパネルモード' },
  { zh: '多人大厅', en: 'Multiplayer Lobby', ja: 'マルチプレイヤーロビー' },
  { zh: '测试中', en: 'IN TESTING', ja: 'テスト中' },
  { zh: '招式表为空！', en: 'This deck is empty!', ja: 'このデッキは空です。' },
  { zh: '请选择招式表', en: 'Select Deck', ja: 'デッキを選択' },
  { zh: '自定义', en: 'Custom', ja: 'カスタム' },
  { zh: '自由出招', en: 'FREE TRICK', ja: 'フリートリック' },
  { zh: '回合结束...', en: 'Round Ended...', ja: 'ラウンド終了...' },
  { zh: '轮换先手中...', en: 'ROTATING STARTER...', ja: '先手を切り替え中...' },
  { zh: '失败', en: 'Fail', ja: '失敗' },
  { zh: '成功', en: 'Success', ja: '成功' },
  { zh: '尝试次数', en: 'Attempts', ja: '試行回数' },
  { zh: '当前回合', en: 'Current Turn', ja: '現在のターン' },
  { zh: '先手', en: 'Setting', ja: '先攻' },
  { zh: '跟招', en: 'Matching', ja: '追従' },
  { zh: '反超', en: 'Stealing', ja: '奪取' },
  { zh: '积分锦标赛', en: 'POINTS TOURNAMENT', ja: 'ポイントトーナメント' },
  { zh: '开始', en: 'Play', ja: '開始' },
  { zh: '开始计时', en: 'Start Round Timer', ja: 'ラウンドタイマー開始' },
  { zh: '设置', en: 'Setup', ja: '設定' },
  { zh: '配置招式与轮次', en: 'Configure Tricks & Rounds', ja: '技とラウンドを設定' },
  { zh: '历史', en: 'History', ja: '履歴' },
  { zh: '查看历史记录', en: 'View Past Results', ja: '過去の結果を見る' },
  { zh: '对战模式', en: 'BATTLE MODE', ja: '対戦モード' },
  { zh: '获胜分数(血量)', en: 'Winning Score', ja: '勝利スコア' },
  { zh: '自由对战', en: 'Free Battle', ja: 'フリーバトル' },
  { zh: '抽招赛对战', en: 'Draw Battle', ja: 'ドローバトル' },
  { zh: '选手自由出招，无需抽卡，由裁判直接判定结果。', en: 'Players choose their own tricks without drawing cards. Judges decide the outcome directly.', ja: '選手はカードを引かずに自由に技を選び、審判が直接結果を判定します。' },
  { zh: '选择自定义招式表，通过随机抽招与对手进行对决。', en: 'Select a custom trick deck and draw random tricks to battle against your opponent.', ja: 'カスタム技デッキを選び、ランダムに技を引いて相手と対戦します。' },
  { zh: '招式表控制中心', en: 'Deck Command Center', ja: 'デッキ管理センター' },
  { zh: '管理', en: 'Manage', ja: '管理' },
  { zh: '请先登录后选择或管理招式表。', en: 'Please log in to select or manage decks.', ja: 'デッキの選択または管理を行うにはログインしてください。' },
  { zh: '登录', en: 'Log In', ja: 'ログイン' },
  { zh: '尚未创建自定义招式表', en: 'No custom decks found', ja: 'カスタムデッキがまだありません' },
  { zh: '新建招式表', en: 'Create New Deck', ja: '新しいデッキを作成' },
  { zh: '牌组', en: 'Decks', ja: 'デッキ' },
  { zh: '空文件夹', en: 'Empty Folder', ja: '空のフォルダー' },
  { zh: '自定义招式表', en: 'Custom Decks', ja: 'カスタムデッキ' },
  { zh: '创建文件夹', en: 'Create Folder', ja: 'フォルダー作成' },
  { zh: '创建招式表', en: 'Create Deck', ja: 'デッキ作成' },
  { zh: '文件夹名称', en: 'Folder Name', ja: 'フォルダー名' },
  { zh: '招式表名称', en: 'Deck Name', ja: 'デッキ名' },
  { zh: '取消', en: 'Cancel', ja: 'キャンセル' },
  { zh: '创建', en: 'Create', ja: '作成' },
  { zh: '暂无自定义招式表或文件夹', en: 'No custom decks or folders', ja: 'カスタムデッキまたはフォルダーがありません' },
  { zh: '编辑自定义招式表', en: 'Editing Custom Deck', ja: 'カスタムデッキ編集中' },
  { zh: '导入 Excel', en: 'Import Excel', ja: 'Excel をインポート' },
  { zh: '新增招式', en: 'New trick', ja: '新しい技' },
  { zh: '招式表为空，快添加一些招式吧！', en: 'Deck is empty. Add some tricks!', ja: 'デッキは空です。技を追加してください。' },
  { zh: '自由式大厅', en: 'FREESTYLE LOBBY', ja: 'フリースタイルロビー' },
  { zh: '创建房间', en: 'Create Room', ja: 'ルーム作成' },
  { zh: '房间名称', en: 'Room Name', ja: 'ルーム名' },
  { zh: '密码（可选）', en: 'Password (Optional)', ja: 'パスワード（任意）' },
  { zh: '刷新', en: 'Refresh', ja: '更新' },
  { zh: '暂无活跃房间', en: 'No active rooms found', ja: 'アクティブルームがありません' },
  { zh: '作为裁判加入', en: 'Join as Judge', ja: '審判として参加' },
  { zh: '删除房间', en: 'Delete Room', ja: 'ルーム削除' },
  { zh: '联机大厅', en: 'ONLINE LOBBY', ja: 'オンラインロビー' },
  { zh: '加入视频对战', en: 'JOIN VIDEO BATTLE', ja: 'ビデオ対戦に参加' },
  { zh: '输入房间 ID', en: 'Enter a room ID', ja: 'ルーム ID を入力' },
  { zh: '连接', en: 'CONNECT', ja: '接続' },
  { zh: '创建房间', en: 'CREATE ROOM', ja: 'ルーム作成' },
  { zh: '活跃房间', en: 'Active Rooms', ja: 'アクティブルーム' },
  { zh: '已上锁', en: 'LOCKED', ja: 'ロック済み' },
  { zh: '加入', en: 'JOIN', ja: '参加' },
  { zh: '全局大厅聊天', en: 'Global Lobby Chat', ja: 'グローバルロビーチャット' },
  { zh: '来做第一个打招呼的人吧！', en: 'Be the first to say hello!', ja: '最初のあいさつをしてみましょう！' },
  { zh: '输入消息', en: 'Type a message', ja: 'メッセージを入力' },
  { zh: '发送', en: 'Send', ja: '送信' },
  { zh: '用户中心', en: 'USER CENTER', ja: 'ユーザーセンター' },
  { zh: '信息', en: 'INFO', ja: '情報' },
  { zh: '修改资料', en: 'PROFILE', ja: 'プロフィール' },
  { zh: '修改密码', en: 'PASSWORD', ja: 'パスワード' },
  { zh: '设置', en: 'SETTINGS', ja: '設定' },
  { zh: '问题反馈', en: 'FEEDBACK', ja: 'フィードバック' },
  { zh: '用户信息', en: 'User Information', ja: 'ユーザー情報' },
  { zh: '邮箱', en: 'Email', ja: 'メールアドレス' },
  { zh: '注册时间', en: 'Joined', ja: '登録日' },
  { zh: '未知', en: 'Unknown', ja: '不明' },
  { zh: '编辑资料', en: 'Edit Profile', ja: 'プロフィール編集' },
  { zh: '昵称', en: 'Display Name', ja: '表示名' },
  { zh: '输入新的昵称', en: 'Enter new display name', ja: '新しい表示名を入力' },
  { zh: '保存中...', en: 'SAVING...', ja: '保存中...' },
  { zh: '保存修改', en: 'SAVE CHANGES', ja: '変更を保存' },
  { zh: '修改密码', en: 'Change Password', ja: 'パスワード変更' },
  { zh: '新密码', en: 'New Password', ja: '新しいパスワード' },
  { zh: '输入新密码', en: 'Enter new password', ja: '新しいパスワードを入力' },
  { zh: '确认密码', en: 'Confirm Password', ja: 'パスワード確認' },
  { zh: '确认新密码', en: 'Confirm new password', ja: '新しいパスワードを確認' },
  { zh: '更新中...', en: 'UPDATING...', ja: '更新中...' },
  { zh: '更新密码', en: 'UPDATE PASSWORD', ja: 'パスワードを更新' },
  { zh: '游戏设置', en: 'Game Settings', ja: 'ゲーム設定' },
  { zh: '主音量', en: 'Master Volume', ja: 'マスター音量' },
  { zh: '提交反馈', en: 'Submit Feedback', ja: 'フィードバック送信' },
  { zh: '反馈内容', en: 'Message', ja: 'メッセージ' },
  { zh: '告诉我们你发现的问题或建议...', en: 'Tell us about bugs, feature requests, or any other feedback...', ja: '不具合、機能要望、その他のご意見をお聞かせください...' },
  { zh: '提交中...', en: 'SUBMITTING...', ja: '送信中...' },
  { zh: '提交反馈', en: 'SUBMIT FEEDBACK', ja: 'フィードバックを送信' },
  { zh: '注册', en: 'Initialize', ja: '登録' },
  { zh: '连接', en: 'Connect', ja: '接続' },
  { zh: '验证码', en: 'Verification Code', ja: '認証コード' },
  { zh: '昵称', en: 'Nickname', ja: 'ニックネーム' },
  { zh: '登录密码', en: 'Access Key', ja: 'ログインキー' },
  { zh: '确认密码', en: 'Confirm Access Key', ja: 'ログインキー確認' },
  { zh: '发送验证码', en: 'SEND CODE', ja: 'コード送信' },
  { zh: '处理中...', en: 'PROCESSING...', ja: '処理中...' },
  { zh: '执行注册', en: 'EXECUTE REGISTRATION', ja: '登録実行' },
  { zh: '建立连接', en: 'ESTABLISH LINK', ja: '接続する' },
  { zh: '已有账号？登录', en: 'Already Registered? Login', ja: 'アカウントをお持ちですか？ログイン' },
  { zh: '新用户？立即注册', en: 'New User? Register Now', ja: '新規ユーザーですか？今すぐ登録' },
  { zh: '回合锦标赛', en: 'TOURNAMENT ROUND', ja: 'トーナメントラウンド' },
  { zh: '预赛 1', en: 'PRELIM 1', ja: '予選 1' },
  { zh: '预赛 2', en: 'PRELIM 2', ja: '予選 2' },
  { zh: '决赛', en: 'FINALS', ja: '決勝' },
  { zh: '开始', en: 'START', ja: '開始' },
  { zh: '退出', en: 'EXIT', ja: '終了' },
  { zh: '准备开始', en: 'GET READY', ja: '準備してください' },
  { zh: '剩余时间', en: 'Time Remaining', ja: '残り時間' },
  { zh: '总分', en: 'Total Points', ja: '合計ポイント' },
  { zh: '跳过', en: 'SKIP', ja: 'スキップ' },
  { zh: '完成本轮', en: 'FINISH ROUND', ja: 'ラウンド終了' },
  { zh: '本轮完成', en: 'ROUND COMPLETE', ja: 'ラウンド完了' },
  { zh: '最终分数', en: 'Final Score', ja: '最終スコア' },
  { zh: '重新挑战当前配置', en: 'Replay Configuration', ja: '設定を再挑戦' },
  { zh: '主菜单', en: 'Main Menu', ja: 'メインメニュー' },
  { zh: '历史记录', en: 'HISTORY', ja: '履歴' },
  { zh: '暂无锦标赛记录', en: 'No tournament records', ja: 'トーナメント記録がありません' },
  { zh: '标准配置', en: 'Standard Profile', ja: '標準プロファイル' },
  { zh: '总计', en: 'Total', ja: '合計' },
  { zh: '决赛初始分', en: 'Initial Final Score', ja: '決勝初期スコア' },
  { zh: '禁止访问', en: 'ACCESS DENIED', ja: 'アクセス拒否' },
  { zh: '你没有权限访问此页面。', en: 'You do not have permission to view this page.', ja: 'このページを表示する権限がありません。' },
  { zh: '返回', en: 'RETURN', ja: '戻る' },
  { zh: '超级管理员', en: 'SUPER ADMIN', ja: 'スーパー管理者' },
  { zh: '积分总表', en: 'POINT LISTS', ja: 'ポイントリスト' },
  { zh: '抽招总表', en: 'DRAW LISTS', ja: 'ドローリスト' },
  { zh: '提交列表', en: 'SUBMISSIONS', ja: '提出一覧' },
  { zh: '加载数据中...', en: 'LOADING DATA...', ja: 'データ読み込み中...' },
  { zh: '暂无反馈。', en: 'No feedback found.', ja: 'フィードバックはありません。' },
  { zh: '用户 ID', en: 'User ID', ja: 'ユーザー ID' },
  { zh: '日期', en: 'Date', ja: '日付' },
  { zh: '视频对战房间', en: 'VIDEO BATTLE ROOM', ja: 'ビデオ対戦ルーム' },
  { zh: '保存房间', en: 'Save Room', ja: 'ルームを保存' },
  { zh: '已保存房间', en: 'Room Saved', ja: '保存済みルーム' },
  { zh: '保存中...', en: 'Saving...', ja: '保存中...' },
  { zh: '退出并删除房间', en: 'Exit & Delete Room', ja: '退出してルーム削除' },
  { zh: '删除中...', en: 'Deleting...', ja: '削除中...' },
  { zh: '成为裁判', en: 'Be Judge', ja: '審判になる' },
  { zh: '成为选手', en: 'Be Player', ja: 'プレイヤーになる' },
  { zh: '作为观众', en: 'Be Audience', ja: '観客になる' },
  { zh: '无摄像头', en: 'NO CAMERA', ja: 'カメラなし' },
  { zh: '摄像头关闭', en: 'CAMERA OFF', ja: 'カメラオフ' },
  { zh: '开启摄像头', en: 'START CAMERA', ja: 'カメラ開始' },
  { zh: '停止', en: 'STOP', ja: '停止' },
  { zh: '获胜者', en: 'Winner', ja: '勝者' },
  { zh: '开始对战', en: 'START BATTLE', ja: '対戦開始' },
  { zh: '对战进行中', en: 'BATTLE IN PROGRESS', ja: '対戦中' },
  { zh: '房间成员', en: 'Room Users', ja: 'ルームユーザー' },
  { zh: '聊天', en: 'Chat', ja: 'チャット' },
  { zh: '和房间里的大家打个招呼吧！', en: 'Say hi to the room!', ja: 'ルームのみんなにあいさつしましょう！' },
  { zh: '胜利！', en: 'VICTORY!', ja: '勝利！' },
  { zh: '开始新对局', en: 'New Game', ja: '新しいゲーム' },
  { zh: '拿下这场比赛', en: 'has won the battle!', ja: 'がこの試合を制しました！' },
  { zh: '自由', en: 'FREE', ja: 'フリー' },
  { zh: '招式', en: 'TRICKS', ja: 'トリック' },
  { zh: '抽取中...', en: 'DRAWING...', ja: 'ドロー中...' }
];

const literalIndex = new Map<string, TranslationEntry>();
for (const entry of literalEntries) {
  literalIndex.set(entry.zh, entry);
  literalIndex.set(entry.en, entry);
  literalIndex.set(entry.ja, entry);
}

const resolveLiteral = (text: string, language: Language) => {
  const entry = literalIndex.get(text);
  return entry ? entry[language] : null;
};

const translateDynamic = (text: string, language: Language): string | null => {
  let match = text.match(/^Status:\s*(.+)$/);
  if (match) {
    const inner = translateTextValue(match[1], language);
    if (language === 'zh') return `状态: ${inner}`;
    if (language === 'ja') return `状態: ${inner}`;
    return `Status: ${inner}`;
  }

  match = text.match(/^Player\s+(\d+)$/);
  if (match) {
    if (language === 'zh') return `玩家 ${match[1]}`;
    if (language === 'ja') return `プレイヤー ${match[1]}`;
    return `Player ${match[1]}`;
  }

  match = text.match(/^Round\s+(\d+)\/3$/);
  if (match) {
    if (language === 'zh') return `第 ${match[1]} 回合 / 3`;
    if (language === 'ja') return `ラウンド ${match[1]} / 3`;
    return `Round ${match[1]}/3`;
  }

  match = text.match(/^\((Setting|Matching|Stealing)\)$/);
  if (match) {
    const value = translateTextValue(match[1], language);
    return `(${value})`;
  }

  match = text.match(/^(.+)\s+starts Round 1$/);
  if (match) {
    if (language === 'zh') return `${match[1]} 开始第 1 回合`;
    if (language === 'ja') return `${match[1]} がラウンド 1 を開始`;
    return `${match[1]} starts Round 1`;
  }

  match = text.match(/^(.+)\s+starts next round$/);
  if (match) {
    if (language === 'zh') return `${match[1]} 先手开始下一回合`;
    if (language === 'ja') return `${match[1]} が次のラウンドを開始`;
    return `${match[1]} starts next round`;
  }

  match = text.match(/^Round\s+(\d+)\/3:\s+(.+)\s+(Success|Fail)\.\s+(.+)'s turn\.$/);
  if (match) {
    const outcome = translateTextValue(match[3], language);
    if (language === 'zh') return `第 ${match[1]} 回合 / 3: ${match[2]} ${outcome}。轮到 ${match[4]}。`;
    if (language === 'ja') return `ラウンド ${match[1]} / 3: ${match[2]} ${outcome}。次は ${match[4]} の番です。`;
    return `Round ${match[1]}/3: ${match[2]} ${match[3]}. ${match[4]}'s turn.`;
  }

  match = text.match(/^Round\s+(\d+)\/3:\s+(.+)\s+Wins!\s+\((Success vs Fail|Fail vs Success)\)$/);
  if (match) {
    const detail = match[3] === 'Success vs Fail'
      ? { zh: '成功 对 失败', ja: '成功 対 失敗', en: 'Success vs Fail' }
      : { zh: '失败 对 成功', ja: '失敗 対 成功', en: 'Fail vs Success' };
    if (language === 'zh') return `第 ${match[1]} 回合 / 3: ${match[2]} 获胜！（${detail.zh}）`;
    if (language === 'ja') return `ラウンド ${match[1]} / 3: ${match[2]} の勝利！（${detail.ja}）`;
    return `Round ${match[1]}/3: ${match[2]} Wins! (${detail.en})`;
  }

  match = text.match(/^Round\s+(\d+)\s+Draw\s+\((Both Success|Both Fail)\)\.\s+Entering Round\s+(\d+)\.\.\.$/);
  if (match) {
    const drawType = match[2] === 'Both Success'
      ? { zh: '双方成功', ja: '両者成功', en: 'Both Success' }
      : { zh: '双方失败', ja: '両者失敗', en: 'Both Fail' };
    if (language === 'zh') return `第 ${match[1]} 回合平局（${drawType.zh}）。进入第 ${match[3]} 回合...`;
    if (language === 'ja') return `ラウンド ${match[1]} は引き分け（${drawType.ja}）。ラウンド ${match[3]} へ...`;
    return `Round ${match[1]} Draw (${drawType.en}). Entering Round ${match[3]}...`;
  }

  match = text.match(/^Round\s+3\s+Draw\s+\((Both Success|Both Fail)\)\.\s+No points awarded\.$/);
  if (match) {
    const drawType = match[1] === 'Both Success'
      ? { zh: '双方成功', ja: '両者成功', en: 'Both Success' }
      : { zh: '双方失败', ja: '両者失敗', en: 'Both Fail' };
    if (language === 'zh') return `第 3 回合平局（${drawType.zh}），不计分。`;
    if (language === 'ja') return `ラウンド 3 は引き分け（${drawType.ja}）。得点は入りません。`;
    return `Round 3 Draw (${drawType.en}). No points awarded.`;
  }

  match = text.match(/^Game Over!\s+(.+)\s+wins!$/);
  if (match) {
    if (language === 'zh') return `游戏结束！${match[1]} 获胜！`;
    if (language === 'ja') return `ゲーム終了！${match[1]} の勝利！`;
    return `Game Over! ${match[1]} wins!`;
  }

  match = text.match(/^Battle Ended!\s+Winner:\s+(.+)$/);
  if (match) {
    if (language === 'zh') return `对战结束！胜者：${match[1]}`;
    if (language === 'ja') return `対戦終了！勝者: ${match[1]}`;
    return `Battle Ended! Winner: ${match[1]}`;
  }

  match = text.match(/^Successfully imported\s+(\d+)\s+tricks!$/);
  if (match) {
    if (language === 'zh') return `成功导入 ${match[1]} 个招式！`;
    if (language === 'ja') return `${match[1]} 件の技を正常にインポートしました。`;
    return `Successfully imported ${match[1]} tricks!`;
  }

  match = text.match(/^Successfully imported\s+(\d+)\s+tricks!\s+\(Max 120 per deck\)$/);
  if (match) {
    if (language === 'zh') return `成功导入 ${match[1]} 个招式！（每组最多 120 个）`;
    if (language === 'ja') return `${match[1]} 件の技を正常にインポートしました。（1 デッキ最大 120 件）`;
    return `Successfully imported ${match[1]} tricks! (Max 120 per deck)`;
  }

  match = text.match(/^Round full\s+\(Max\s+(\d+)\)\.$/);
  if (match) {
    if (language === 'zh') return `本轮已满（最多 ${match[1]} 个）。`;
    if (language === 'ja') return `このラウンドは上限です（最大 ${match[1]}）。`;
    return `Round full (Max ${match[1]}).`;
  }

  match = text.match(/^Target round is full\s+\(Max\s+(\d+)\)\.$/);
  if (match) {
    if (language === 'zh') return `目标轮次已满（最多 ${match[1]} 个）。`;
    if (language === 'ja') return `対象ラウンドは上限です（最大 ${match[1]}）。`;
    return `Target round is full (Max ${match[1]}).`;
  }

  match = text.match(/^Server error\s+\((\d+)\)\.$/);
  if (match) {
    if (language === 'zh') return `服务器错误（${match[1]}）。`;
    if (language === 'ja') return `サーバーエラー（${match[1]}）。`;
    return `Server error (${match[1]}).`;
  }

  match = text.match(/^Non-JSON response\s+\((\d+)\)\.$/);
  if (match) {
    if (language === 'zh') return `服务器返回非 JSON（${match[1]}）。`;
    if (language === 'ja') return `非 JSON レスポンスです（${match[1]}）。`;
    return `Non-JSON response (${match[1]}).`;
  }

  match = text.match(/^Error parsing Excel file \/ 解析Excel出错:\s+(.+)$/);
  if (match) {
    if (language === 'zh') return `解析 Excel 出错: ${match[1]}`;
    if (language === 'ja') return `Excel 解析エラー: ${match[1]}`;
    return `Error parsing Excel file: ${match[1]}`;
  }

  return null;
};

function translateTextValue(text: string, language: Language): string {
  const normalized = text.replace(/\u00a0/g, ' ').trim();
  if (!normalized) return text;

  const literal = resolveLiteral(normalized, language);
  if (literal) return literal;

  const dynamic = translateDynamic(normalized, language);
  if (dynamic) return dynamic;

  if (normalized.includes(' / ')) {
    const parts = normalized.split(' / ');
    const latinPart = parts.find(part => /[A-Za-z]/.test(part));
    const chinesePart = parts.find(part => /[\u4e00-\u9fff]/.test(part));

    if (language === 'en') {
      return translateTextValue(latinPart ?? parts[0], language);
    }

    if (language === 'zh') {
      return translateTextValue(chinesePart ?? parts[0], language);
    }

    return translateTextValue(latinPart ?? chinesePart ?? parts[0], language);
  }

  return normalized;
}

const applyTranslatedText = (source: string, language: Language) => {
  const trimmed = source.replace(/\u00a0/g, ' ').trim();
  if (!trimmed) return source;
  const translated = translateTextValue(trimmed, language);
  return source.replace(trimmed, translated);
};

const I18nContext = createContext<I18nContextValue | null>(null);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 进入 App 时：优先用户手动选择过的语言，否则按系统语言选择语言版本。
  const [language, setLanguageState] = useState<Language>(() => (
    readRememberedLanguage() ?? detectBrowserLanguage() ?? 'en'
  ));

  const languageRef = useRef(language);
  const originalAlertRef = useRef<typeof window.alert | null>(null);
  const originalConfirmRef = useRef<typeof window.confirm | null>(null);
  const textNodeOriginalsRef = useRef(new WeakMap<Text, string>());
  const elementOriginalTextRef = useRef(new WeakMap<HTMLElement, string>());

  // 只有用户主动切换语言时才写入存储，自动判断结果不落盘。
  const setLanguage = useCallback((nextLanguage: Language) => {
    setLanguageState(nextLanguage);
    try {
      window.localStorage.setItem(STORAGE_KEY, nextLanguage);
      window.localStorage.setItem(MANUAL_STORAGE_KEY, 'true');
    } catch {
      // 隐私模式等场景下 localStorage 不可用，忽略即可。
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const nativeLanguage = await detectNativeLanguage();
      if (cancelled || !nativeLanguage) return;
      // 用户已经手动选过语言就不再覆盖。
      if (readRememberedLanguage()) return;
      setLanguageState(previous => (previous === nativeLanguage ? previous : nativeLanguage));
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    languageRef.current = language;
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : language === 'ja' ? 'ja' : 'en';
  }, [language]);

  useEffect(() => {
    if (!originalAlertRef.current) originalAlertRef.current = window.alert.bind(window);
    if (!originalConfirmRef.current) originalConfirmRef.current = window.confirm.bind(window);

    window.alert = (message?: unknown) => {
      const raw = typeof message === 'string' ? message : String(message ?? '');
      originalAlertRef.current?.(translateTextValue(raw, languageRef.current));
    };

    window.confirm = (message?: string) => {
      const raw = typeof message === 'string' ? message : String(message ?? '');
      return originalConfirmRef.current?.(translateTextValue(raw, languageRef.current)) ?? false;
    };

    return () => {
      if (originalAlertRef.current) window.alert = originalAlertRef.current;
      if (originalConfirmRef.current) window.confirm = originalConfirmRef.current;
    };
  }, []);

  useEffect(() => {
    const ignoredTags = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA']);
    const attributeNames = ['placeholder', 'title', 'aria-label', 'alt'];
    const nonDedupTags = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'IMG', 'SVG', 'PATH', 'BUTTON']);

    const normalizeDisplayText = (value: string | null | undefined) => {
      return (value ?? '').replace(/\s+/g, ' ').trim();
    };

    const shouldSkipTranslation = (element: Element | null) => {
      return !!element?.closest('[data-i18n-skip="true"]');
    };

    const isSimpleTextElement = (element: Element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (nonDedupTags.has(element.tagName)) return false;
      if (element.children.length > 0) {
        return Array.from(element.children).every(child => child.tagName === 'BR');
      }
      return normalizeDisplayText(element.textContent).length > 0;
    };

    const restoreCollapsedElements = (elements: HTMLElement[]) => {
      for (const element of elements) {
        if (element.dataset.i18nCollapsed === 'true') {
          const originalDisplay = element.dataset.i18nOriginalDisplay;
          if (originalDisplay) {
            element.style.display = originalDisplay;
          } else {
            element.style.removeProperty('display');
          }
          delete element.dataset.i18nCollapsed;
        }
      }
    };

    const getOriginalNodeText = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return textNodeOriginalsRef.current.get(node as Text) ?? node.textContent ?? '';
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (elementOriginalTextRef.current.has(element)) {
          return elementOriginalTextRef.current.get(element) ?? element.textContent ?? '';
        }

        return Array.from(node.childNodes)
          .map(child => getOriginalNodeText(child))
          .join('');
      }

      return node.textContent ?? '';
    };

    const restoreCollapsedLineNodes = (root: ParentNode) => {
      if (!(root instanceof Element || root instanceof Document || root instanceof DocumentFragment)) return;

      const collapsedElements = root.querySelectorAll<HTMLElement>('[data-i18n-line-collapsed="true"]');
      collapsedElements.forEach(element => {
        const originalDisplay = element.dataset.i18nOriginalDisplay;
        if (originalDisplay) {
          element.style.display = originalDisplay;
        } else {
          element.style.removeProperty('display');
        }
        delete element.dataset.i18nLineCollapsed;
      });

      const collapsedBreaks = root.querySelectorAll<HTMLElement>('[data-i18n-line-break-collapsed="true"]');
      collapsedBreaks.forEach(element => {
        const originalDisplay = element.dataset.i18nOriginalDisplay;
        if (originalDisplay) {
          element.style.display = originalDisplay;
        } else {
          element.style.removeProperty('display');
        }
        delete element.dataset.i18nLineBreakCollapsed;
      });
    };

    const collapseDuplicateLinesWithinElement = (root: ParentNode) => {
      if (!(root instanceof Element)) return;

      restoreCollapsedLineNodes(root);

      const containers = [root, ...Array.from(root.querySelectorAll('*'))];

      for (const container of containers) {
        if (!(container instanceof HTMLElement)) continue;
        if (nonDedupTags.has(container.tagName)) continue;

        const hasBreak = Array.from(container.childNodes).some(node => node.nodeName === 'BR');
        if (!hasBreak) continue;

        const segments: Array<{ nodes: Node[]; breakNode?: HTMLBRElement }> = [];
        let currentSegment: { nodes: Node[]; breakNode?: HTMLBRElement } = { nodes: [] };

        for (const child of Array.from(container.childNodes)) {
          if (child.nodeName === 'BR') {
            segments.push(currentSegment);
            currentSegment = { nodes: [], breakNode: child as HTMLBRElement };
            continue;
          }

          currentSegment.nodes.push(child);
        }
        segments.push(currentSegment);

        let previousVisibleText = '';
        let previousOriginalText = '';

        for (const segment of segments) {
          const visibleText = normalizeDisplayText(
            segment.nodes.map(node => node.textContent ?? '').join(' ')
          );
          const originalText = normalizeDisplayText(
            segment.nodes.map(node => getOriginalNodeText(node)).join(' ')
          );

          if (!visibleText) continue;

          if (
            previousVisibleText &&
            visibleText === previousVisibleText &&
            originalText &&
            previousOriginalText &&
            originalText !== previousOriginalText
          ) {
            if (segment.breakNode) {
              if (segment.breakNode.dataset.i18nOriginalDisplay === undefined) {
                segment.breakNode.dataset.i18nOriginalDisplay = segment.breakNode.style.display || '';
              }
              segment.breakNode.style.display = 'none';
              segment.breakNode.dataset.i18nLineBreakCollapsed = 'true';
            }

            for (const node of segment.nodes) {
              if (node.nodeType === Node.TEXT_NODE) {
                (node as Text).nodeValue = '';
              } else if (node instanceof HTMLElement) {
                if (node.dataset.i18nOriginalDisplay === undefined) {
                  node.dataset.i18nOriginalDisplay = node.style.display || '';
                }
                node.style.display = 'none';
                node.dataset.i18nLineCollapsed = 'true';
              }
            }
            continue;
          }

          previousVisibleText = visibleText;
          previousOriginalText = originalText;
        }
      }
    };

    const collapseDuplicateSiblings = (root: ParentNode) => {
      const containerElements = root instanceof Element
        ? [root, ...Array.from(root.querySelectorAll('*'))]
        : [];

      for (const container of containerElements) {
        const directChildren = Array.from(container.children)
          .filter((child): child is HTMLElement => child instanceof HTMLElement);

        if (directChildren.length < 2) continue;

        restoreCollapsedElements(directChildren);

        let previousVisibleText = '';
        let previousOriginalText = '';

        for (const child of directChildren) {
          if (!isSimpleTextElement(child)) {
            previousVisibleText = '';
            previousOriginalText = '';
            continue;
          }

          const visibleText = normalizeDisplayText(child.textContent);
          const originalText = normalizeDisplayText(elementOriginalTextRef.current.get(child) ?? visibleText);

          if (
            visibleText &&
            previousVisibleText &&
            visibleText === previousVisibleText &&
            originalText &&
            previousOriginalText &&
            originalText !== previousOriginalText
          ) {
            if (child.dataset.i18nOriginalDisplay === undefined) {
              child.dataset.i18nOriginalDisplay = child.style.display || '';
            }
            child.style.display = 'none';
            child.dataset.i18nCollapsed = 'true';
            continue;
          }

          previousVisibleText = visibleText;
          previousOriginalText = originalText;
        }
      }
    };

    const translateNode = (root: Node) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let current = walker.nextNode();

      while (current) {
        const textNode = current as Text;
        const parentElement = textNode.parentElement;
        if (
          parentElement &&
          !ignoredTags.has(parentElement.tagName) &&
          parentElement.tagName !== 'INPUT' &&
          !parentElement.isContentEditable &&
          !shouldSkipTranslation(parentElement)
        ) {
          if (!textNodeOriginalsRef.current.has(textNode)) {
            textNodeOriginalsRef.current.set(textNode, textNode.nodeValue ?? '');
          }

          const original = textNodeOriginalsRef.current.get(textNode) ?? '';
          const translated = applyTranslatedText(original, language);
          if (textNode.nodeValue !== translated) {
            textNode.nodeValue = translated;
          }

          if (
            parentElement instanceof HTMLElement &&
            parentElement.children.length === 0 &&
            normalizeDisplayText(original)
          ) {
            if (!elementOriginalTextRef.current.has(parentElement)) {
              elementOriginalTextRef.current.set(parentElement, original);
            }
          }
        }
        current = walker.nextNode();
      }

      if (root instanceof Element) {
        const elements = [root, ...Array.from(root.querySelectorAll('*'))].filter(
          (element): element is HTMLElement => element instanceof HTMLElement
        );
        for (const element of elements) {
          if (shouldSkipTranslation(element)) continue;
          for (const attribute of attributeNames) {
            const value = element.getAttribute(attribute);
            if (!value) continue;

            const datasetKey = `i18nOrig${attribute.replace(/(^|-)([a-z])/g, (_, __, letter) => letter.toUpperCase())}`;
            if (!(datasetKey in element.dataset)) {
              element.dataset[datasetKey] = value;
            }

            const original = element.dataset[datasetKey] ?? value;
            const translated = translateTextValue(original, language);
            if (translated !== value) {
              element.setAttribute(attribute, translated);
            }
          }
        }

        collapseDuplicateLinesWithinElement(root);
        collapseDuplicateSiblings(root);
      }
    };

    translateNode(document.body);

    // 单独处理文本节点的动态更新：React 更新文案时只会改写 textNode.nodeValue，
    // 这属于 characterData 变更，而 translateNode 用 TreeWalker 从文本节点根出发
    // 走不到任何子节点（等于空操作），于是动态文案会一直停留在源语言。
    // 这里重新登记原文并翻译，保证「先渲染、后更新」的文案也能跟随语言。
    const translateUpdatedTextNode = (textNode: Text) => {
      const parentElement = textNode.parentElement;
      if (
        !parentElement ||
        ignoredTags.has(parentElement.tagName) ||
        parentElement.isContentEditable ||
        shouldSkipTranslation(parentElement)
      ) {
        return;
      }

      const current = textNode.nodeValue ?? '';
      const cached = textNodeOriginalsRef.current.get(textNode);
      // 我们自己写回的译文会再次触发 characterData 回调：
      // 此时节点值等于「已知原文的译文」，直接跳过，避免反复翻译。
      if (cached !== undefined && translateTextValue(cached, language) === current) return;

      // 外部（React 等）写入了新的源文案，更新原文记录后再翻译。
      textNodeOriginalsRef.current.set(textNode, current);
      const translated = applyTranslatedText(current, language);
      if (translated !== current) {
        textNode.nodeValue = translated;
      }
    };

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') {
          if (mutation.target.nodeType === Node.TEXT_NODE) {
            translateUpdatedTextNode(mutation.target as Text);
          } else {
            translateNode(mutation.target);
          }
        }

        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.ELEMENT_NODE) {
            translateNode(node);
          }
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });

    return () => observer.disconnect();
  }, [language]);

  const value = useMemo<I18nContextValue>(() => ({
    language,
    setLanguage,
    translateText: (text: string) => translateTextValue(text, language),
    t: (key: string) => uiText[key]?.[language] ?? key
  }), [language, setLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};
